/**
 * useOAuth2RedirectFlow Hook
 *
 * React hook for managing OAuth2 authorization using full page redirect (no popup).
 * More reliable than popup flow - works on all devices, no popup blockers.
 */

import { notifications } from "@mantine/notifications";
import { useState, useCallback, useEffect, useRef } from "react";
import { subscribeToEmailAccount, getEmailAccount } from "../firestore";
import { startOAuth2Authorization, buildOAuth2RedirectUrl } from "../functions";
import type { EmailAccount } from "../schema/email-account.types";

/**
 * SessionStorage key for OAuth2 flow state
 */
const OAUTH2_STATE_KEY = "oauth2_flow_state";

/**
 * OAuth2 Flow State (stored in sessionStorage)
 */
interface OAuth2FlowState {
  accountId: string;
  startedAt: number;
  returnUrl: string; // URL to return to after OAuth2 completes
}

/**
 * Hook result interface
 */
interface UseOAuth2RedirectFlowResult {
  /** Whether OAuth2 flow is in progress */
  isAuthorizing: boolean;
  /** Start OAuth2 authorization flow (redirects page) */
  startAuthorization: (accountId: string) => Promise<void>;
  /** Reset flow state */
  reset: () => void;
}

/**
 * Save OAuth2 flow state to sessionStorage before redirecting
 * @param state
 */
const saveFlowState = (state: OAuth2FlowState): void => {
  try {
    sessionStorage.setItem(OAUTH2_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error("Failed to save OAuth2 flow state:", error);
  }
};

/**
 * Load OAuth2 flow state from sessionStorage (after redirect back)
 */
const loadFlowState = (): OAuth2FlowState | null => {
  try {
    const stored = sessionStorage.getItem(OAUTH2_STATE_KEY);
    if (!stored) return null;

    return JSON.parse(stored) as OAuth2FlowState;
  } catch (error) {
    console.error("Failed to load OAuth2 flow state:", error);
    return null;
  }
};

/**
 * Clear OAuth2 flow state from sessionStorage
 */
const clearFlowState = (): void => {
  try {
    sessionStorage.removeItem(OAUTH2_STATE_KEY);
  } catch (error) {
    console.error("Failed to clear OAuth2 flow state:", error);
  }
};

/**
 * useOAuth2RedirectFlow Hook
 *
 * Manages OAuth2 authorization flow using full page redirect.
 * Much simpler and more reliable than popup flow.
 *
 * Flow:
 * 1. User clicks "Authorize" → saves state → redirects to OAuth2
 * 2. User completes OAuth2 → callback updates Firestore → redirects back
 * 3. This hook detects return → subscribes to account → shows success/error
 * @returns Hook result with authorization state and actions
 * @example
 * function ConnectAccountButton({ accountId }: { accountId: string }) {
 *   const { isAuthorizing, startAuthorization } = useOAuth2RedirectFlow();
 *
 *   return (
 *     <Button
 *       loading={isAuthorizing}
 *       onClick={() => startAuthorization(accountId)}
 *     >
 *       Conectar
 *     </Button>
 *   );
 * }
 */
export function useOAuth2RedirectFlow(): UseOAuth2RedirectFlowResult {
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const unsubscribeReference = useRef<(() => void) | null>(null);
  const hasShownNotificationReference = useRef(false);

  /**
   * Reset flow state
   */
  const reset = useCallback(() => {
    clearFlowState();
    setIsAuthorizing(false);
    hasShownNotificationReference.current = false;

    if (unsubscribeReference.current) {
      unsubscribeReference.current();
      unsubscribeReference.current = null;
    }
  }, []);

  /**
   * Handle account status changes from Firestore subscription
   */
  const handleAccountUpdate = useCallback(
    (account: EmailAccount | null) => {
      if (!account) return;

      // Prevent showing duplicate notifications
      if (hasShownNotificationReference.current) {
        return;
      }

      // Check if account is now connected (OAuth2 completed successfully)
      if (account.status === "connected") {
        hasShownNotificationReference.current = true;

        notifications.show({
          title: "✅ Conta conectada",
          message: `Email ${account.email} conectado com sucesso!`,
          color: "green"
        });

        reset();
      }

      // Check if OAuth2 failed
      if (account.status === "error") {
        hasShownNotificationReference.current = true;

        const errorDetail = account.error_message || account.last_error || "Erro desconhecido";

        notifications.show({
          title: "❌ Erro na autorização",
          message: `Falha ao conectar: ${errorDetail}`,
          color: "red",
          autoClose: false
        });

        reset();
      }
    },
    [reset]
  );

  /**
   * On mount: check if we're returning from OAuth2 redirect
   */
  useEffect(() => {
    const flowState = loadFlowState();

    if (!flowState) return;

    setIsAuthorizing(true);

    const checkInitialState = async () => {
      try {
        const account = await getEmailAccount(flowState.accountId);
        if (account && hasShownNotificationReference.current === false) {
          handleAccountUpdate(account);
        }
      } catch (error) {
        console.error("Failed to get initial account state:", error);
      }
    };

    checkInitialState().catch((error) => {
      console.error("Failed to check initial account state:", error);
    });

    try {
      const unsubscribe = subscribeToEmailAccount(flowState.accountId, handleAccountUpdate);
      unsubscribeReference.current = unsubscribe;
    } catch (error) {
      console.error("Failed to subscribe to account:", error);

      notifications.show({
        title: "❌ Erro",
        message: "Não foi possível monitorar status da conta. Recarregue a página.",
        color: "red"
      });

      reset();
    }

    return () => {
      if (unsubscribeReference.current) {
        unsubscribeReference.current();
      }
    };
  }, [handleAccountUpdate, reset]);

  /**
   * Start OAuth2 authorization flow (redirects page)
   */
  const startAuthorization = useCallback(async (accountId: string): Promise<void> => {
    try {
      setIsAuthorizing(true);

      // Get authorization URL from Cloud Function
      const response = await startOAuth2Authorization(accountId, {
        success_redirect_url: buildOAuth2RedirectUrl("success"),
        error_redirect_url: buildOAuth2RedirectUrl("error")
      });

      const { authorization_url } = response;

      // Save state before redirecting
      saveFlowState({
        accountId,
        startedAt: Date.now(),
        returnUrl: window.location.href
      });

      // Redirect to OAuth2 authorization page
      window.location.href = authorization_url;

      // NOTE: Code after this won't execute - we're leaving the page!
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Erro ao iniciar autorização";

      console.error("Failed to start OAuth2 authorization:", error);

      setIsAuthorizing(false);

      notifications.show({
        title: "Erro ao iniciar autorização",
        message: errorMessage,
        color: "red",
        autoClose: false
      });
    }
  }, []);

  return {
    isAuthorizing,
    startAuthorization,
    reset
  };
}
